export const WEBSITE_EMPTY_MESSAGE = 'Please enter your website (e.g. acme.com). RightMessage needs a site to run on.'

export const WEBSITE_INVALID_MESSAGE = "That doesn't look like a website address. Try something like acme.com."

export const WEBSITE_SOCIAL_MESSAGE =
  'Enter your own website, like acme.com. RightMessage runs on your site, not a social or link-in-bio page.'

export const WEBSITE_DOMAIN_MESSAGES = {
  empty: WEBSITE_EMPTY_MESSAGE,
  invalid: WEBSITE_INVALID_MESSAGE,
  social: WEBSITE_SOCIAL_MESSAGE
} as const

export type WebsiteDomainIssue = keyof typeof WEBSITE_DOMAIN_MESSAGES

export type WebsiteDomainResult = { ok: true; website: string } | { ok: false; issue: WebsiteDomainIssue }

/**
 * Profile and link-in-bio hosts. RightMessage's snippet has to run on the visitor's own site.
 * Matched against the registrable host and any parent (l.instagram.com, m.facebook.com).
 */
const SOCIAL_AND_LINK_IN_BIO_HOSTS = new Set([
  'instagram.com',
  'facebook.com',
  'fb.com',
  'fb.me',
  'tiktok.com',
  'youtube.com',
  'youtu.be',
  'linktr.ee',
  'x.com',
  'twitter.com',
  'linkedin.com',
  'pinterest.com',
  'snapchat.com',
  'threads.net',
  'beacons.ai',
  'bio.link',
  'lnk.bio',
  'linkin.bio',
  'allmylinks.com',
  'stan.store',
  'campsite.bio',
  'solo.to',
  'hoo.be',
  'taplink.cc',
  'msha.ke',
  'withkoji.com',
  'linkpop.com',
  'bento.me',
  'about.me'
])

/** Validate the original input before URL parsing can discard or rewrite forbidden URL parts. */
export function inspectWebsiteDomain(value: string): WebsiteDomainResult {
  const trimmed = value.trim()
  if (trimmed === '') {
    return { ok: false, issue: 'empty' }
  }

  if (/[\p{Cc}\p{Cf}]/u.test(value) || /\s/u.test(trimmed) || hasUnsupportedScheme(trimmed)) {
    return { ok: false, issue: 'invalid' }
  }

  const remainder = trimmed.replace(/^https?:\/\//i, '')
  if (remainder.startsWith('//') || remainder.includes('\\')) {
    return { ok: false, issue: 'invalid' }
  }

  const separator = remainder.search(/[/?#]/)
  const hostPort = separator === -1 ? remainder : remainder.slice(0, separator)
  if (
    hostPort === '' ||
    hostPort.includes('@') ||
    hostPort.includes(':') ||
    hostPort.includes('%') ||
    hostPort.endsWith('.')
  ) {
    return { ok: false, issue: 'invalid' }
  }

  const host = stripLeadingWww(hostPort)
  if (!/^[\p{L}\p{M}\p{N}.-]+$/u.test(host) || !host.includes('.')) {
    return { ok: false, issue: 'invalid' }
  }

  try {
    const hostname = new URL(`https://${host}`).hostname
    const labels = hostname.split('.')
    const tld = labels[labels.length - 1] ?? ''
    if (
      hostname.length > 253 ||
      labels.some((label) => !/^[a-z\d](?:[a-z\d-]{0,61}[a-z\d])?$/.test(label)) ||
      /^\d+$/.test(tld)
    ) {
      return { ok: false, issue: 'invalid' }
    }

    if (isSocialOrLinkInBioHost(hostname)) {
      return { ok: false, issue: 'social' }
    }

    return { ok: true, website: `https://${hostname}` }
  } catch {
    return { ok: false, issue: 'invalid' }
  }
}

export function normalizeWebsiteDomain(value: string): string | null {
  const result = inspectWebsiteDomain(value)
  return result.ok ? result.website : null
}

/** Bare domain to show in the website field after a successful normalisation. */
export function websiteDomainForInput(value: string): string | null {
  const normalized = normalizeWebsiteDomain(value)
  return normalized === null ? null : normalized.slice('https://'.length)
}

export function websiteDomainFailureMessage(value: string): string | null {
  const result = inspectWebsiteDomain(value)
  return result.ok ? null : WEBSITE_DOMAIN_MESSAGES[result.issue]
}

export function isUnchangedWebsite(value: string, currentWebsite: string | null | undefined): boolean {
  return (
    currentWebsite !== null &&
    currentWebsite !== undefined &&
    (value === currentWebsite || value === currentWebsite.replace(/^https?:\/\//i, ''))
  )
}

function hasUnsupportedScheme(value: string): boolean {
  return /^[a-z][a-z0-9+.-]*:/i.test(value) && !/^https?:\/\//i.test(value)
}

/** Drop a single leading www label when a real domain remains (`www.acme.com`, not `www.com`). */
function stripLeadingWww(host: string): string {
  if (!/^www\./i.test(host)) {
    return host
  }

  const remainder = host.slice(4)
  return remainder.includes('.') ? remainder : host
}

function isSocialOrLinkInBioHost(hostname: string): boolean {
  const labels = hostname.split('.')
  for (let index = 0; index < labels.length - 1; index += 1) {
    const candidate = labels.slice(index).join('.')
    if (SOCIAL_AND_LINK_IN_BIO_HOSTS.has(candidate)) {
      return true
    }
  }

  return false
}
